import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Backdrop,
  Slide
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';
import { Calendar, MapPin, Clock, DollarSign, TrendingUp, Plus, ChevronUp, Edit2, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import EventCreationDialog from './EventCreationDialog';

// Category icons mapping
const categoryIcons = {
  'Cultural & Historical': '🏛️',
  'Arts & Entertainment': '🎭',
  'Culinary & Gastronomy': '🍽️',
  'Outdoor & Adventure': '🏔️',
  'Nature & Wildlife': '🦁',
  'Relaxation & Wellness': '🧘',
  'Sports & Fitness': '⚽',
  'Shopping & Markets': '🛍️',
  'Nightlife & Social': '🌃',
  'Events & Festivals': '🎉',
  'Guided Tours & Excursions': '🚌',
  'Educational & Workshops': '📚',
  'Family & Kids': '👨‍👩‍👧‍👦',
  'Photography & Scenic': '📸',
  'Spiritual & Religious': '🕉️',
  'Volunteer & Community': '🤝',
  'Unique & Offbeat': '✨',
  'Transportation': '✈️',
  'Others': '📌',
  // Legacy categories for backward compatibility
  'flight': '✈️',
  'hotel': '🏨',
  'activity': '🎯',
  'restaurant': '🍽️',
  'meal': '🍽️',
  'accommodation': '🏨',
  'other': '📌'
};

const TripDetails = ({ open, onClose, tripData = null, onTripUpdate }) => {
  const [animationIn, setAnimationIn] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [newExpense, setNewExpense] = useState('');
  const [expandedEvents, setExpandedEvents] = useState({});
  
  // Event creation dialog state
  const [openEventDialog, setOpenEventDialog] = useState(false);
  
  // Local events state to track added events
  const [localEvents, setLocalEvents] = useState([]);
  const [currentTripId, setCurrentTripId] = useState(null);
  
  // Inline editing state
  const [isEditing, setIsEditing] = useState({
    title: false,
    budget: false,
    startDate: false,
    endDate: false
  });
  
  // Local editable data state
  const [editableData, setEditableData] = useState({
    title: '',
    totalBudget: 0,
    startDate: '',
    endDate: ''
  });
  
  
  // Global edit mode state
  const [isGlobalEditMode, setIsGlobalEditMode] = useState(false);
  
  // Calendar state
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  useEffect(() => {
    if (open) {
      // Check if we're viewing a different trip
      const tripId = tripData?._id || tripData?.id;
      if (tripId !== currentTripId) {
        // Reset local events only when switching trips
        setLocalEvents([]);
        setCurrentTripId(tripId);
      }
      
      // Initialize editable data with current trip data
      setEditableData({
        title: tripData?.title || 'Trip Details',
        totalBudget: tripData?.totalBudget || 0,
        startDate: tripData?.startDate || '',
        endDate: tripData?.endDate || ''
      });
      
      // Small delay to ensure proper animation
      const timer = setTimeout(() => {
        setAnimationIn(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setAnimationIn(false);
      // Reset editing states but keep local events
      setIsEditing({ title: false, budget: false, startDate: false, endDate: false });
      setIsGlobalEditMode(false);
    }
  }, [open, tripData, currentTripId]);

  const handleClose = () => {
    setAnimationIn(false);
    // Wait for animation to complete before closing
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getDuration = () => {
    if (!tripData?.startDate || !tripData?.endDate) {
      return tripData?.dayCount ? `${tripData.dayCount} Days` : '1 Day';
    }
    const start = new Date(tripData.startDate);
    const end = new Date(tripData.endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return `${days} Days`;
  };

  const getProgressPercentage = () => {
    if (!tripData?.totalBudget || tripData.totalBudget === 0) return 0;
    return Math.min(Math.round((tripData.currentSpent / tripData.totalBudget) * 100), 100);
  };

  const handleAddExpense = () => {
    if (newExpense && !isNaN(newExpense)) {
      alert(`Added $${newExpense} to expenses`);
      setNewExpense('');
      setShowExpenseModal(false);
    }
  };

  const toggleEventExpansion = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  // Event creation dialog handlers
  const handleOpenEventDialog = () => {
    setOpenEventDialog(true);
  };

  const handleCloseEventDialog = () => {
    setOpenEventDialog(false);
  };

  const handleEventSubmit = async (eventData) => {
    try {
      // First check if tripData has an ID
      if (!tripData?._id && !tripData?.id) {
        alert('Error: Trip data is missing. Please refresh and try again.');
        return;
      }

      // Import the API function
      const { addEvent } = await import('../services/api');
      
      // Call the API to save the event to the backend
      const response = await addEvent(tripData._id || tripData.id, eventData);
      
      // Create the new event with the response data
      const newEvent = {
        ...eventData,
        _id: response.data._id || response.data.id || Date.now().toString(),
        day: eventData.day || 1
      };
      
      // Update the parent's tripData state directly
      if (onTripUpdate) {
        const updatedTripData = {
          ...tripData,
          events: [...(tripData.events || []), newEvent]
        };
        onTripUpdate(updatedTripData);
      } else {
        // Fallback to local state if no callback provided
        setLocalEvents(prev => [...prev, newEvent]);
      }
    } catch (error) {
      console.error('Error creating event:', error);
      
      // Create local fallback event
      const newEvent = {
        ...eventData,
        _id: Date.now().toString(),
        day: eventData.day || 1,
        isLocal: true // Mark as local-only for debugging
      };
      
      // Try to update parent state even for failed API calls
      if (onTripUpdate) {
        const updatedTripData = {
          ...tripData,
          events: [...(tripData.events || []), newEvent]
        };
        onTripUpdate(updatedTripData);
      } else {
        setLocalEvents(prev => [...prev, newEvent]);
      }
      
      // Show error but allow local usage
      alert(`Event "${eventData.title}" created locally, but could not be saved to server. It may be lost when you refresh the page.`);
    }
  };

  // Inline editing handlers
  const startEditing = (field) => {
    setIsEditing(prev => ({ ...prev, [field]: true }));
  };

  const cancelEditing = (field) => {
    setIsEditing(prev => ({ ...prev, [field]: false }));
    // Reset to original value
    setEditableData(prev => ({
      ...prev,
      [field]: field === 'title' ? (tripData?.title || 'Trip Details') :
               field === 'totalBudget' ? (tripData?.totalBudget || 0) :
               field === 'startDate' ? (tripData?.startDate || '') :
               field === 'endDate' ? (tripData?.endDate || '') : prev[field]
    }));
  };

  const saveEditing = (field) => {
    setIsEditing(prev => ({ ...prev, [field]: false }));
    // Here you would typically save to backend
  };

  const handleInputChange = (field, value) => {
    setEditableData(prev => ({ ...prev, [field]: value }));
  };


  // Global edit mode handlers
  const activateGlobalEditMode = () => {
    setIsGlobalEditMode(true);
    setIsEditing({
      title: true,
      budget: true,
      startDate: true,
      endDate: true
    });
  };

  const deactivateGlobalEditMode = () => {
    setIsGlobalEditMode(false);
    setIsEditing({
      title: false,
      budget: false,
      startDate: false,
      endDate: false
    });
  };

  if (!open) return null;

  // Use real trip data primarily, fallback to local events if no callback provided
  const originalEvents = tripData?.events || [];
  const events = onTripUpdate ? originalEvents : [...originalEvents, ...localEvents];
  const hasValidDates = tripData?.startDate && tripData?.endDate;

  // Calendar helper functions
  const getTravelDates = () => {
    if (!hasValidDates) return [];
    
    const startDate = new Date(tripData.startDate);
    const endDate = new Date(tripData.endDate);
    const dates = [];
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }
    return dates;
  };

  const travelDates = getTravelDates();
  
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollToDirection = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Get events for selected date, sorted by start time
  const getEventsForDate = (dateIndex) => {
    const dayNumber = dateIndex + 1;
    const dayEvents = events.filter(event => event.day === dayNumber);
    
    // Sort events by start time
    return dayEvents.sort((a, b) => {
      // Handle events without start time - put them at the end
      if (!a.start && !b.start) return 0;
      if (!a.start) return 1;
      if (!b.start) return -1;
      
      // Parse time strings - robust parsing for both 12-hour and 24-hour formats
      const parseTime = (timeStr) => {
        if (!timeStr) return new Date(0);
        
        const cleanTimeStr = timeStr.trim();
        const hasAmPm = /[AP]M/i.test(cleanTimeStr);
        
        if (hasAmPm) {
          // Handle 12-hour format: "11:40 AM", "6:45 PM"
          const match = cleanTimeStr.match(/(\d{1,2}):(\d{2})\s*([AP]M)/i);
          if (!match) return new Date(0);
          
          let [, hours, minutes, period] = match;
          hours = parseInt(hours);
          minutes = parseInt(minutes);
          
          // Convert to 24-hour format
          if (period.toUpperCase() === 'PM' && hours !== 12) {
            hours += 12;
          } else if (period.toUpperCase() === 'AM' && hours === 12) {
            hours = 0;
          }
          
          return new Date(2000, 0, 1, hours, minutes);
        } else {
          // Handle 24-hour format: "14:30", "08:15"
          const match = cleanTimeStr.match(/(\d{1,2}):(\d{2})/);
          if (!match) return new Date(0);
          
          const [, hours, minutes] = match;
          return new Date(2000, 0, 1, parseInt(hours), parseInt(minutes));
        }
      };
      
      const timeA = parseTime(a.start);
      const timeB = parseTime(b.start);
      
      return timeA - timeB;
    });
  };

  const selectedDateEvents = getEventsForDate(selectedDateIndex);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
          }
        }
      }}
      disableAutoFocus={false}
      disableEnforceFocus={false}
      disableRestoreFocus={false}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: { xs: '10px', sm: '20px' },
        overflow: 'auto',
        paddingTop: { xs: '20px', sm: '40px' }
      }}
    >
      <Slide direction="up" in={animationIn} timeout={300}>
        <Box 
          tabIndex={-1}
          sx={{
            minHeight: 'auto',
            maxHeight: 'calc(100vh - 20px)',
            width: '100%',
            maxWidth: { xs: '100%', sm: '95%', md: '1200px' },
            position: 'relative',
            overflow: 'auto',
            borderRadius: { xs: '10px', sm: '20px' },
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1)',
            marginBottom: '10px',
            outline: 'none',
            background: 'linear-gradient(135deg, #ff9a8b 0%, #ff6b9d 25%, #c44569 50%, #f8b500 75%, #ffa726 100%)',
          }}
        >
          {/* Header with Close, Edit, and Save Buttons */}
          <Box sx={{
            position: 'absolute',
            top: { xs: 10, sm: 15, md: 20 },
            right: { xs: 10, sm: 15, md: 20 },
            zIndex: 1000, // High z-index to ensure it's always on top
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            {/* Save Button - only show in global edit mode */}
            {isGlobalEditMode && (
              <IconButton 
                onClick={() => {
                  // Save all changes
                  saveEditing('title');
                  saveEditing('budget');
                  saveEditing('startDate');
                  saveEditing('endDate');
                  deactivateGlobalEditMode();
                }}
                sx={{ 
                  backgroundColor: 'rgba(16, 185, 129, 0.2)',
                  color: '#10b981',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.3)',
                    transform: 'scale(1.05)',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
                  },
                  '&:active': {
                    transform: 'scale(0.95)'
                  },
                  width: { xs: 36, sm: 40, md: 44 },
                  height: { xs: 36, sm: 40, md: 44 },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <Check size={18} />
              </IconButton>
            )}
            {/* Edit Button */}
            <IconButton 
              onClick={isGlobalEditMode ? deactivateGlobalEditMode : activateGlobalEditMode}
              sx={{ 
                backgroundColor: isGlobalEditMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                color: isGlobalEditMode ? '#10b981' : 'white',
                backdropFilter: 'blur(8px)',
                border: `1px solid ${isGlobalEditMode ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
                '&:hover': {
                  backgroundColor: isGlobalEditMode ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.3)',
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                },
                '&:active': {
                  transform: 'scale(0.95)'
                },
                width: { xs: 36, sm: 40, md: 44 },
                height: { xs: 36, sm: 40, md: 44 },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <Edit2 size={18} />
            </IconButton>

            {/* Close Button */}
            <IconButton 
              onClick={handleClose}
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                },
                '&:active': {
                  transform: 'scale(0.95)'
                },
                width: { xs: 36, sm: 40, md: 44 },
                height: { xs: 36, sm: 40, md: 44 },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 20, sm: 24, md: 26 } }} />
            </IconButton>
          </Box>

          {/* Trip Overview Section */}
          <Box sx={{ 
            maxWidth: '1400px', 
            mx: 'auto', 
            px: { xs: 2, sm: 3, lg: 4 }, 
            py: { xs: 4, sm: 6, lg: 8 },
            // Add top padding to prevent overlap with close button
            pt: { xs: 7, sm: 8, lg: 10 }
          }}>
            <Box sx={{
              backgroundColor: 'white',
              borderRadius: { xs: '24px', sm: '32px' },
              boxShadow: '0 32px 64px rgba(0, 0, 0, 0.15)',
              p: { xs: 3, sm: 4, md: 6, lg: 8 },
              border: '1px solid rgba(255, 165, 0, 0.1)',
              mb: { xs: 4, sm: 6 }
            }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 3, sm: 4, md: 6 }, mb: { xs: 3, sm: 4, md: 6 } }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {isEditing.title ? (
                      <Box sx={{ width: '100%' }}>
                        <input
                          type="text"
                          value={editableData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          style={{
                            fontSize: 'clamp(1.25rem, 4vw, 2.5rem)',
                            fontWeight: 700,
                            color: '#333',
                            border: '2px solid #ff9a8b',
                            borderRadius: '6px',
                            padding: 'clamp(6px, 2vw, 12px)',
                            width: '100%',
                            maxWidth: '100%',
                            outline: 'none',
                            background: 'rgba(255, 255, 255, 0.9)',
                            boxSizing: 'border-box',
                            fontFamily: 'inherit',
                            lineHeight: '1.2',
                            minHeight: 'clamp(40px, 8vw, 60px)'
                          }}
                          autoFocus
                        />
                      </Box>
                    ) : (
                      <Typography 
                        variant="h2" 
                        sx={{
                          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #ff9a8b 0%, #ff6b9d 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          letterSpacing: '-0.5px',
                          width: '100%',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 }
                        }}
                        onClick={() => startEditing('title')}
                      >
                        {editableData.title}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666', mb: 2 }}>
                    <MapPin size={16} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {tripData?.destination || 'Destination'}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {tripData?.description || 'Explore your adventure details and timeline.'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{
                    background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%)',
                    borderRadius: { xs: '12px', sm: '16px', md: '20px' },
                    p: { xs: 1.5, sm: 2, md: 3 },
                    group: true,
                    minHeight: { xs: 'auto', sm: '120px' }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Calendar size={16} color="#ff9a8b" />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#333' }}>
                        Duration
                      </Typography>
                    </Box>
                    {isEditing.startDate || isEditing.endDate ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 1 }, width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' }, color: '#333', width: { xs: '35px', sm: '40px' }, flexShrink: 0 }}>
                            Start:
                          </Typography>
                          <input
                            type="date"
                            value={editableData.startDate}
                            onChange={(e) => handleInputChange('startDate', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setIsEditing(prev => ({ ...prev, startDate: false, endDate: true }));
                              }
                              if (e.key === 'Escape') cancelEditing('startDate');
                            }}
                            style={{
                              fontSize: 'clamp(0.625rem, 2.5vw, 0.75rem)',
                              color: '#333',
                              border: '1px solid #ff9a8b',
                              borderRadius: '4px',
                              padding: 'clamp(2px, 1vw, 6px)',
                              width: 'clamp(80px, 25vw, 120px)',
                              maxWidth: '120px',
                              outline: 'none',
                              background: 'white',
                              boxSizing: 'border-box',
                              minHeight: '32px'
                            }}
                            autoFocus={isEditing.startDate}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' }, color: '#333', width: { xs: '35px', sm: '40px' }, flexShrink: 0 }}>
                            End:
                          </Typography>
                          <input
                            type="date"
                            value={editableData.endDate}
                            onChange={(e) => handleInputChange('endDate', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                saveEditing('startDate');
                                saveEditing('endDate');
                              }
                              if (e.key === 'Escape') {
                                cancelEditing('startDate');
                                cancelEditing('endDate');
                              }
                            }}
                            style={{
                              fontSize: 'clamp(0.625rem, 2.5vw, 0.75rem)',
                              color: '#333',
                              border: '1px solid #ff9a8b',
                              borderRadius: '4px',
                              padding: 'clamp(2px, 1vw, 6px)',
                              width: 'clamp(80px, 25vw, 120px)',
                              maxWidth: '120px',
                              outline: 'none',
                              background: 'white',
                              boxSizing: 'border-box',
                              minHeight: '32px'
                            }}
                            autoFocus={isEditing.endDate}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontSize: { xs: '1.25rem', sm: '1.5rem' }, 
                            fontWeight: 700, 
                            color: '#333',
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.8 }
                          }}
                          onClick={() => startEditing('startDate')}
                        >
                          {getDuration()}
                        </Typography>
                        {hasValidDates && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '0.75rem', 
                              color: '#666', 
                              mt: 0.5, 
                              display: 'block',
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }}
                            onClick={() => startEditing('startDate')}
                          >
                            {formatDate(editableData.startDate).split(',')[0]}, {formatDate(editableData.startDate).split(',')[1]}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                  
                  <Box sx={{
                    background: 'linear-gradient(135deg, #fff0f5 0%, #ffe0eb 100%)',
                    borderRadius: { xs: '12px', sm: '16px', md: '20px' },
                    p: { xs: 1.5, sm: 2, md: 3 },
                    group: true,
                    minHeight: { xs: 'auto', sm: '120px' }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <DollarSign size={16} color="#ff6b9d" />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#333' }}>
                        Budget
                      </Typography>
                    </Box>
                    {isEditing.budget ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, width: '100%' }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: '#333', flexShrink: 0 }}>
                          $
                        </Typography>
                        <input
                          type="number"
                          value={editableData.totalBudget}
                          onChange={(e) => handleInputChange('totalBudget', Number(e.target.value))}
                          style={{
                            fontSize: 'clamp(0.875rem, 3vw, 1rem)',
                            fontWeight: 600,
                            color: '#333',
                            border: '2px solid #ff6b9d',
                            borderRadius: '6px',
                            padding: 'clamp(3px, 1.5vw, 8px)',
                            outline: 'none',
                            background: 'white',
                            width: 'clamp(70px, 20vw, 100px)',
                            maxWidth: '100px',
                            boxSizing: 'border-box',
                            minHeight: '32px'
                          }}
                          autoFocus
                        />
                      </Box>
                    ) : (
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontSize: { xs: '1.25rem', sm: '1.5rem' }, 
                          fontWeight: 700, 
                          color: '#333',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 }
                        }}
                        onClick={() => startEditing('budget')}
                      >
                        ${tripData?.currentSpent || 0} / ${editableData.totalBudget}
                      </Typography>
                    )}
                    <Box
                      onClick={() => setShowExpenseModal(true)}
                      sx={{
                        fontSize: '0.75rem',
                        color: '#ff6b9d',
                        '&:hover': { color: '#e55a87' },
                        mt: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer'
                      }}
                    >
                      <Plus size={12} />
                      Add expense
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Progress Bar */}
              <Box sx={{ mt: { xs: 3, sm: 4, md: 6 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp size={16} color="#10b981" />
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#333' }}>
                      Trip Progress
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#333' }}>
                    {getProgressPercentage()}%
                  </Typography>
                </Box>
                <Box sx={{
                  width: '100%',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '9999px',
                  height: '12px',
                  overflow: 'hidden'
                }}>
                  <Box 
                    sx={{
                      height: '100%',
                      background: 'linear-gradient(90deg, #ff9a8b 0%, #ff6b9d 100%)',
                      borderRadius: '9999px',
                      transition: 'all 0.5s ease',
                      width: `${getProgressPercentage()}%`
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Calendar Timeline Section */}
          <Box sx={{ position: 'relative', py: { xs: 6, sm: 8, md: 12 } }}>
            <Box sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 2, sm: 3, lg: 4 } }}>
              <Typography variant="h3" sx={{
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                fontWeight: 700,
                color: 'white',
                mb: { xs: 4, sm: 6, md: 8 },
                textAlign: 'center'
              }}>
                Your Itinerary Timeline
              </Typography>

              {/* Calendar Date Selection */}
              {travelDates.length > 0 ? (
                <>
                  {/* Date Header */}
                  <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
                    <Typography variant="h6" sx={{
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      fontWeight: 600,
                      color: 'white',
                      mb: 1
                    }}>
                      Select Your Travel Date
                    </Typography>
                    <Typography variant="body2" sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      color: 'rgba(255, 255, 255, 0.8)'
                    }}>
                      Choose a date to view your itinerary
                    </Typography>
                  </Box>

                  {/* Slidable Calendar */}
                  <Box sx={{ position: 'relative', mb: { xs: 4, sm: 6 } }}>
                    {/* Left Arrow */}
                    {showLeftArrow && (
                      <IconButton
                        onClick={() => scrollToDirection('left')}
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          color: '#ff9a8b',
                          width: { xs: 32, sm: 40 },
                          height: { xs: 32, sm: 40 },
                          backdropFilter: 'blur(8px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            transform: 'translateY(-50%) scale(1.05)',
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)'
                          }
                        }}
                      >
                        <ChevronLeft size={16} />
                      </IconButton>
                    )}

                    {/* Right Arrow */}
                    {showRightArrow && (
                      <IconButton
                        onClick={() => scrollToDirection('right')}
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          color: '#ff9a8b',
                          width: { xs: 32, sm: 40 },
                          height: { xs: 32, sm: 40 },
                          backdropFilter: 'blur(8px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            transform: 'translateY(-50%) scale(1.05)',
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)'
                          }
                        }}
                      >
                        <ChevronRight size={16} />
                      </IconButton>
                    )}

                    {/* Dates Container */}
                    <Box
                      ref={scrollContainerRef}
                      onScroll={handleScroll}
                      sx={{
                        display: 'flex',
                        gap: { xs: 1, sm: 2 },
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                        px: { xs: 5, sm: 6 },
                        py: 2
                      }}
                    >
                      {travelDates.map((date, index) => {
                        const isSelected = selectedDateIndex === index;
                        const dayEvents = getEventsForDate(index);
                        const hasEvents = dayEvents.length > 0;
                        const isToday = new Date().toDateString() === date.toDateString();

                        return (
                          <Box
                            key={index}
                            onClick={() => setSelectedDateIndex(index)}
                            sx={{
                              flexShrink: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              p: { xs: 1.5, sm: 2 },
                              borderRadius: { xs: '12px', sm: '16px' },
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              backgroundColor: isSelected ? '#ff9a8b' : isToday ? 'rgba(255, 228, 225, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                              color: isSelected ? 'white' : '#2c3e50',
                              border: `2px solid ${isSelected ? '#ff9a8b' : hasEvents ? '#ffb6a3' : 'rgba(255, 255, 255, 0.3)'}`,
                              minWidth: { xs: '60px', sm: '70px' },
                              backdropFilter: 'blur(8px)',
                              boxShadow: isSelected ? '0 8px 24px rgba(255, 154, 139, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                              transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                              '&:hover': {
                                transform: isSelected ? 'scale(1.05)' : 'scale(1.02)',
                                boxShadow: isSelected ? '0 8px 24px rgba(255, 154, 139, 0.4)' : '0 6px 16px rgba(0, 0, 0, 0.15)'
                              }
                            }}
                          >
                            <Typography sx={{
                              fontSize: { xs: '0.625rem', sm: '0.75rem' },
                              fontWeight: 500,
                              opacity: 0.8,
                              mb: 0.5
                            }}>
                              {date.toLocaleDateString('en-US', { weekday: 'short' })}
                            </Typography>
                            <Typography sx={{
                              fontSize: { xs: '1.125rem', sm: '1.25rem' },
                              fontWeight: 700,
                              mb: hasEvents ? 0.5 : 0
                            }}>
                              {date.getDate()}
                            </Typography>
                            {hasEvents && (
                              <Box sx={{
                                width: { xs: 4, sm: 5 },
                                height: { xs: 4, sm: 5 },
                                borderRadius: '50%',
                                backgroundColor: isSelected ? 'white' : '#ff9a8b'
                              }} />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Selected Date Content */}
                  <Box sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: { xs: '20px', sm: '24px' },
                    p: { xs: 3, sm: 4, md: 5 },
                    boxShadow: '0 32px 64px rgba(0, 0, 0, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Calendar size={20} style={{ color: '#ff9a8b' }} />
                      <Typography variant="h5" sx={{
                        fontSize: { xs: '1.125rem', sm: '1.25rem' },
                        fontWeight: 700,
                        color: '#2c3e50'
                      }}>
                        {travelDates[selectedDateIndex]?.toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </Typography>
                    </Box>

                    {/* Add Event Button - only show in global edit mode */}
                    {isGlobalEditMode && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, sm: 4 } }}>
                        <Box
                          onClick={handleOpenEventDialog}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: { xs: 1.5, sm: 2 },
                            px: { xs: 3, sm: 6 },
                            py: { xs: 1.5, sm: 2.5 },
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            borderRadius: '50px',
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', sm: '1.125rem' },
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                            border: 'none',
                            textDecoration: 'none',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)',
                              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                            },
                            '&:active': {
                              transform: 'translateY(0px)',
                              boxShadow: '0 6px 24px rgba(16, 185, 129, 0.3)'
                            }
                          }}
                        >
                          <Plus size={18} />
                          Add New Event
                        </Box>
                      </Box>
                    )}

                    {/* Events for Selected Date */}
                    {selectedDateEvents.length > 0 ? (
                      <Box sx={{ position: 'relative' }}>
                        {selectedDateEvents.map((event, index) => {
                          const isExpanded = expandedEvents[event._id];
                          const eventDate = new Date(event.start || tripData?.startDate || Date.now());

                          return (
                            <Box key={event._id || index} sx={{ 
                              display: 'flex', 
                              mb: { xs: 3, sm: 4 }, 
                              position: 'relative'
                            }}>
                              {/* Desktop: Time Column */}
                              <Box sx={{ 
                                display: { xs: 'none', sm: 'block' },
                                width: '100px', 
                                textAlign: 'right', 
                                pr: 3,
                                flexShrink: 0
                              }}>
                                {event.start && (
                                  <Typography sx={{ 
                                    color: '#ff9a8b', 
                                    fontWeight: 700, 
                                    fontSize: '0.875rem',
                                    lineHeight: 1.3
                                  }}>
                                    {formatTime(event.start)}
                                  </Typography>
                                )}
                              </Box>

                              {/* Desktop: Timeline Line and Icon */}
                              <Box sx={{ 
                                display: { xs: 'none', sm: 'block' },
                                position: 'relative', 
                                flexShrink: 0
                              }}>
                                {/* Vertical connecting line */}
                                <Box sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: '2px',
                                  height: '100%',
                                  backgroundColor: 'rgba(255, 154, 139, 0.3)'
                                }}>
                                  {index < selectedDateEvents.length - 1 && (
                                    <Box sx={{
                                      position: 'absolute',
                                      top: '40px',
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      backgroundColor: 'rgba(255, 154, 139, 0.3)'
                                    }} />
                                  )}
                                </Box>

                                {/* Timeline icon */}
                                <Box sx={{
                                  position: 'relative',
                                  zIndex: 10,
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  backgroundColor: 'white',
                                  border: '2px solid #ff9a8b'
                                }}>
                                  <span style={{ fontSize: '14px' }}>
                                    {categoryIcons[event.category] || categoryIcons['Others']}
                                  </span>
                                </Box>
                              </Box>

                              {/* Content Card */}
                              <Box sx={{ 
                                flex: 1, 
                                ml: { xs: 0, sm: 3 }
                              }}>
                                <Box 
                                  sx={{
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    p: 3,
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                    border: '1px solid rgba(255, 154, 139, 0.1)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': { 
                                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                                      transform: 'translateY(-2px)'
                                    }
                                  }}
                                  onClick={() => toggleEventExpansion(event._id)}
                                >
                                  {/* Mobile: Time display */}
                                  <Box sx={{ 
                                    display: { xs: 'flex', sm: 'none' },
                                    alignItems: 'center',
                                    mb: 1.5,
                                    gap: 1
                                  }}>
                                    <Box sx={{
                                      width: '20px',
                                      height: '20px',
                                      borderRadius: '50%',
                                      backgroundColor: '#ff9a8b',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0
                                    }}>
                                      <span style={{ fontSize: '10px' }}>
                                        {categoryIcons[event.category] || categoryIcons['Others']}
                                      </span>
                                    </Box>
                                    {event.start && (
                                      <Typography sx={{ 
                                        color: '#ff9a8b', 
                                        fontWeight: 700, 
                                        fontSize: '0.875rem'
                                      }}>
                                        {formatTime(event.start)}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Header with title and category */}
                                  <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'flex-start', 
                                    mb: 1,
                                    gap: 1
                                  }}>
                                    <Typography variant="h6" sx={{
                                      fontSize: '1.125rem',
                                      fontWeight: 700,
                                      color: '#2c3e50',
                                      lineHeight: 1.3,
                                      flex: 1,
                                      pr: 1
                                    }}>
                                      {event.title}
                                    </Typography>

                                    <Box sx={{
                                      px: 2,
                                      py: 0.5,
                                      borderRadius: '12px',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      color: 'white',
                                      flexShrink: 0,
                                      backgroundColor: '#ff9a8b',
                                      maxWidth: '120px',
                                      textAlign: 'center',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {event.category || 'Others'}
                                    </Box>
                                  </Box>

                                  <Typography sx={{
                                    fontSize: '0.875rem',
                                    color: '#666',
                                    mb: 2,
                                    lineHeight: 1.6
                                  }}>
                                    {isExpanded 
                                      ? event.description 
                                      : (event.description?.substring(0, 100) + (event.description?.length > 100 ? '...' : ''))
                                    }
                                  </Typography>

                                  <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                    fontSize: '0.875rem'
                                  }}>
                                    {event.location && (
                                      <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        color: '#666',
                                        minWidth: 0,
                                        flex: '1 1 auto'
                                      }}>
                                        <MapPin size={14} style={{ marginRight: '6px', flexShrink: 0 }} />
                                        <span style={{ 
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}>{event.location}</span>
                                      </Box>
                                    )}
                                    {event.duration && (
                                      <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        color: '#666',
                                        flexShrink: 0
                                      }}>
                                        <Clock size={14} style={{ marginRight: '6px' }} />
                                        <span>{event.duration}</span>
                                      </Box>
                                    )}
                                  </Box>

                                  {isExpanded && (
                                    <Box sx={{
                                      mt: 2,
                                      pt: 2,
                                      borderTop: '1px solid #e5e7eb'
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                          Click to collapse
                                        </Typography>
                                        <ChevronUp size={16} color="#9ca3af" />
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
                        <Box sx={{
                          width: { xs: '60px', sm: '80px' },
                          height: { xs: '60px', sm: '80px' },
                          mx: 'auto',
                          mb: { xs: 2, sm: 3 },
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 154, 139, 0.1)'
                        }}>
                          <Calendar size={24} style={{ color: '#ff9a8b' }} />
                        </Box>
                        <Typography variant="h6" sx={{
                          fontSize: { xs: '1rem', sm: '1.125rem' },
                          fontWeight: 600,
                          color: '#666',
                          mb: 1,
                          px: { xs: 2, sm: 0 }
                        }}>
                          No events scheduled for this date
                        </Typography>
                        <Typography variant="body2" sx={{
                          color: '#999',
                          mb: { xs: 2.5, sm: 3 },
                          fontSize: { xs: '0.85rem', sm: '0.875rem' },
                          px: { xs: 3, sm: 0 }
                        }}>
                          Add your first event to start planning this day
                        </Typography>
                        {isGlobalEditMode && (
                          <Box
                            onClick={handleOpenEventDialog}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: { xs: 1, sm: 1.5 },
                              px: { xs: 3, sm: 4 },
                              py: { xs: 1.5, sm: 2 },
                              background: '#ff9a8b',
                              color: 'white',
                              borderRadius: '50px',
                              fontWeight: 600,
                              fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 24px rgba(255, 154, 139, 0.3)'
                              }
                            }}
                          >
                            <Plus size={16} />
                            Add Event
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                /* No Travel Dates */
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '400px',
                  textAlign: 'center'
                }}>
                  <Box sx={{
                    width: '120px',
                    height: '120px',
                    mx: 'auto',
                    mb: 3,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '3px dashed rgba(255, 255, 255, 0.3)'
                  }}>
                    <Calendar size={48} style={{ color: 'white' }} />
                  </Box>
                  <Typography variant="h4" sx={{
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    fontWeight: 700,
                    color: 'white',
                    mb: 2
                  }}>
                    Set Your Travel Dates
                  </Typography>
                  <Typography variant="body1" sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: 4,
                    maxWidth: '400px'
                  }}>
                    Add your start and end dates to see your trip timeline and start planning your daily itinerary.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Expense Modal */}
          {showExpenseModal && (
            <Box sx={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
              p: 2
            }}>
              <Box sx={{
                backgroundColor: 'white',
                borderRadius: '20px',
                p: 3,
                maxWidth: '400px',
                width: '100%'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#333', mb: 2 }}>
                  Add Expense
                </Typography>
                <input
                  type="number"
                  value={newExpense}
                  onChange={(e) => setNewExpense(e.target.value)}
                  placeholder="Enter amount"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    marginBottom: '16px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.outline = '2px solid #ff9a8b';
                    e.target.style.borderColor = '#ff9a8b';
                  }}
                  onBlur={(e) => {
                    e.target.style.outline = 'none';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box
                    onClick={handleAddExpense}
                    sx={{
                      flex: 1,
                      background: 'linear-gradient(90deg, #ff9a8b 0%, #ff6b9d 100%)',
                      color: 'white',
                      py: 1,
                      borderRadius: '8px',
                      fontWeight: 600,
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #ff8a7a 0%, #ff5a8d 100%)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Add
                  </Box>
                  <Box
                    onClick={() => setShowExpenseModal(false)}
                    sx={{
                      flex: 1,
                      backgroundColor: '#e5e7eb',
                      color: '#374151',
                      py: 1,
                      borderRadius: '8px',
                      fontWeight: 600,
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#d1d5db' },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Cancel
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Event Creation Dialog */}
          <EventCreationDialog
            open={openEventDialog}
            onClose={handleCloseEventDialog}
            onSubmit={handleEventSubmit}
          />
        </Box>
      </Slide>
    </Modal>
  );
};

export default TripDetails;